/**
 * In-Memory Message Bus
 *
 * EventEmitter-based communication between client and server.
 * Replaces WebSocket with direct method calls while maintaining message envelope format.
 */
import { EventEmitter } from 'events';
/**
 * Message bus for client-server communication
 */
export class MessageBus extends EventEmitter {
    constructor() {
        super();
        this.clientTransport = null;
        this.messageHistory = [];
        this.MAX_HISTORY = 1000;
    }
    /**
     * Connect client transport to the bus
     */
    connectClient(transport) {
        this.clientTransport = transport;
        // Forward messages from client to server
        transport.on('message', (data) => {
            try {
                const envelope = JSON.parse(data);
                this.recordMessage('client->server', envelope);
                this.emit('client-message', envelope);
            }
            catch (error) {
                console.error('Error parsing client message:', error);
            }
        });
        // Forward messages from server to client
        transport.on('receive', (envelope) => {
            this.recordMessage('server->client', envelope);
            this.emit('server-message', envelope);
        });
        // Handle connection events
        transport.on('open', () => {
            this.emit('client-connected');
        });
        transport.on('close', () => {
            this.emit('client-disconnected');
        });
        transport.on('error', (error) => {
            this.emit('client-error', error);
        });
    }
    /**
     * Disconnect client transport
     */
    disconnectClient() {
        if (this.clientTransport) {
            this.clientTransport.removeAllListeners();
            this.clientTransport = null;
        }
    }
    /**
     * Record message in history
     */
    recordMessage(direction, envelope) {
        this.messageHistory.push({
            ...envelope,
            // Add direction metadata
            payload: {
                ...envelope.payload,
                _direction: direction,
            },
        });
        // Trim history if too large
        if (this.messageHistory.length > this.MAX_HISTORY) {
            this.messageHistory.shift();
        }
        // Emit for visualization
        this.emit('message-recorded', {
            direction,
            envelope,
            timestamp: Date.now(),
        });
    }
    /**
     * Get message history
     */
    getMessageHistory() {
        return [...this.messageHistory];
    }
    /**
     * Clear message history
     */
    clearHistory() {
        this.messageHistory = [];
        this.emit('history-cleared');
    }
    /**
     * Get message statistics
     */
    getStatistics() {
        const stats = {
            totalMessages: this.messageHistory.length,
            messagesByType: {},
            clientToServer: 0,
            serverToClient: 0,
        };
        for (const msg of this.messageHistory) {
            // Count by type
            stats.messagesByType[msg.type] = (stats.messagesByType[msg.type] || 0) + 1;
            // Count by direction
            const direction = msg.payload?._direction;
            if (direction === 'client->server') {
                stats.clientToServer++;
            }
            else if (direction === 'server->client') {
                stats.serverToClient++;
            }
        }
        return stats;
    }
    /**
     * Filter messages by type
     */
    getMessagesByType(type) {
        return this.messageHistory.filter((msg) => msg.type === type);
    }
    /**
     * Filter messages by direction
     */
    getMessagesByDirection(direction) {
        return this.messageHistory.filter((msg) => {
            const msgDirection = msg.payload?._direction;
            return msgDirection === direction;
        });
    }
    /**
     * Get recent messages (last N)
     */
    getRecentMessages(count) {
        return this.messageHistory.slice(-count);
    }
}
/**
 * Create a connected message bus with client and server
 */
export function createMessageBus(transport) {
    const bus = new MessageBus();
    bus.connectClient(transport);
    return bus;
}
//# sourceMappingURL=message-bus.js.map